import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { View, Text, NavLink } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { DropdownField } from "../../components/base/DropdownField";
import { InputField } from "../../components/base/InputField";
import { simulationAgentActions } from "../../redux/reducers/simulationAgentSlice";
import { agentActions } from "../../redux/reducers/agentSlice";
import { useAppDispatch, useAppSelector } from "../../redux";
import { ConfirmDialog } from "../../components/ConfirmDialog";

type SimulationAgentFormValues = {
  name: string;
  systemPrompt: string;
  agentId: string;
};

const defaultFormState: SimulationAgentFormValues = {
  name: "",
  systemPrompt: "",
  agentId: "",
};

export const SimulationAgentDetailsPage = () => {
  const { simulationAgentId } = useParams<{ simulationAgentId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const simulationAgent = useAppSelector((state) => (simulationAgentId ? state.simulationAgent.entities[simulationAgentId] : undefined));
  const isLoading = useAppSelector((state) => state.simulationAgent.isLoading);
  const isSaving = useAppSelector((state) => state.simulationAgent.isLoading);
  const isDeleting = useAppSelector((state) => state.simulationAgent.isLoadingDelete);

  const agents = useAppSelector((state) => Object.values(state.agent.entities));
  const agentsIsLoading = useAppSelector((state) => state.agent.isLoading);

  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState<SimulationAgentFormValues>(defaultFormState);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    if (simulationAgentId) {
      dispatch(simulationAgentActions.getById({ id: simulationAgentId }));
    }
    dispatch(agentActions.get({}));
  }, [dispatch, simulationAgentId]);

  useEffect(() => {
    if (simulationAgent) {
      setForm({
        name: simulationAgent.name || "",
        systemPrompt: simulationAgent.systemPrompt || "",
        agentId: simulationAgent.agentId || "",
      });
    }
  }, [simulationAgent]);

  const handleFormChange =
    <K extends keyof SimulationAgentFormValues>(key: K) =>
    (value: SimulationAgentFormValues[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    };

  const handleCancel = () => {
    navigate("/agents/simulations");
  };

  const handleSave = async () => {
    if (isSaving || !simulationAgentId) {
      return;
    }

    try {
      const name = form.name.trim();
      if (!name) {
        throw new Error("Name is required.");
      }

      if (!form.agentId) {
        throw new Error("Please select an agent.");
      }

      setFormError(null);
      await dispatch(
        simulationAgentActions.updateById({
          id: simulationAgentId,
          name,
          systemPrompt: form.systemPrompt,
          agentId: form.agentId,
        }),
      ).unwrap();
      navigate("/agents/simulations");
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Unable to save simulation agent");
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!simulationAgentId || !simulationAgent) {
      return;
    }

    try {
      await dispatch(simulationAgentActions.deleteById({ id: simulationAgentId })).unwrap();
      navigate("/agents/simulations");
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Unable to delete simulation agent");
      setShowDeleteDialog(false);
    }
  };

  if (isLoading && !simulationAgent) {
    return (
      <View className="flex flex-col flex-1 w-full min-h-full gap-6 p-6 bg-white dark:bg-slate-900 overflow-y-auto">
        <Text className="text-gray-500 dark:text-gray-400">Loading...</Text>
      </View>
    );
  }

  if (!simulationAgent) {
    return (
      <View className="flex flex-col flex-1 w-full min-h-full gap-6 p-6 bg-white dark:bg-slate-900 overflow-y-auto">
        <Text className="text-gray-500 dark:text-gray-400">Simulation agent not found.</Text>
      </View>
    );
  }

  return (
    <View className="flex flex-col flex-1 w-full min-h-full gap-6 p-6 bg-white dark:bg-slate-900 overflow-y-auto">
      <NavLink
        href="/agents/simulations"
        className="flex items-center w-fit p-0"
      >
        <Icon
          name="arrow-back"
          size={18}
          className="mr-1"
        />
        Simulations
      </NavLink>

      <View className="flex flex-col gap-6 w-full">
        <View className="flex flex-col gap-2">
          <Text className="text-2xl font-semibold text-gray-900 dark:text-white">Edit Simulation Agent</Text>
          <Text className="text-sm text-gray-600 dark:text-gray-400">Update the simulation agent's configuration.</Text>
        </View>

        {formError ? (
          <View className="rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-3">
            <Text className="text-sm text-red-700 dark:text-red-300">{formError}</Text>
          </View>
        ) : null}

        <View className="flex flex-col gap-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-6 shadow-sm w-full">
          <InputField
            label="Name"
            placeholder="Enter simulation agent name"
            value={form.name}
            onChange={handleFormChange("name")}
          />

          <DropdownField
            label="Agent"
            options={agents.map((agent) => ({
              value: agent.id,
              label: agent.name,
            }))}
            value={form.agentId}
            onChange={handleFormChange("agentId")}
            placeholder="Select an agent"
            readOnly={agentsIsLoading}
          />
          {agentsIsLoading && <Text className="text-xs text-gray-500 dark:text-gray-400 -mt-2">Loading agents...</Text>}

          <InputField
            label="System Prompt"
            placeholder="Describe the simulation agent's behavior"
            value={form.systemPrompt}
            onChange={handleFormChange("systemPrompt")}
            type="textarea"
            className="min-h-[128px]"
          />
        </View>

        <View className="flex flex-row gap-3 justify-between">
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50"
          >
            {isDeleting ? (
              <Icon
                name="loader"
                size={14}
                className="animate-spin"
              />
            ) : (
              <Icon
                name="trash"
                size={14}
              />
            )}
            Delete
          </button>
          <View className="flex flex-row gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-50"
            >
              {isSaving ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="check"
                  size={14}
                />
              )}
              Save Changes
            </button>
          </View>
        </View>

        <ConfirmDialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={confirmDelete}
          title="Delete Simulation Agent"
          message={`Are you sure you want to delete "${simulationAgent?.name}"?`}
          isLoading={isDeleting}
        >
          <Text className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone. The simulation agent will be permanently removed.</Text>
        </ConfirmDialog>
      </View>
    </View>
  );
};
